import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { TooltipProvider } from './components/ui/tooltip';
import { PreferencesProvider } from './contexts/PreferencesContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { RecordingProvider } from './contexts/RecordingContext';
import { TranscriptionProvider } from './contexts/TranscriptionContext';
import { CelebrationProvider } from './contexts/CelebrationContext';
import { Toaster } from './components/ui/sonner';
import OfflineIndicator from './components/OfflineIndicator';
import AppUpdateBanner from './components/AppUpdateBanner';
import SWUpdatePrompt from './components/SWUpdatePrompt';
import PaywallModal from './components/PaywallModal';
import { installPaywallInterceptor } from './lib/paywall';

// Catch every HTTP 402 anywhere in the app and surface the upgrade prompt.
// Idempotent — safe across hot-reloads.
installPaywallInterceptor();
import QuickCapture from './components/QuickCapture';
import WhatsNewBanner from './components/WhatsNewBanner';
import CapacitorDeepLinkHandler from './components/CapacitorDeepLinkHandler';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import GoogleOAuthCallback from './pages/GoogleOAuthCallback';
import MobilePage from './pages/MobilePage';
// iter191 — hoist SupportChatFab here so pre-login users on /login,
// /, /mobile, /pricing can also reach AI support.
import SupportChatFab from './components/SupportChatFab';
import MagicLinkPage from './pages/MagicLinkPage';
import OnboardingPage from './pages/OnboardingPage';
import LandingPage from './pages/LandingPageV3';
import DevelopersPage from './pages/DevelopersPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import AboutPage from './pages/AboutPage';
import BookmarkReviewPage from './pages/BookmarkReviewPage';
import { BlogListPage, BlogPostPage } from './pages/BlogPage';
import LegalPage from './pages/LegalPage';
import PaymentCallbackPage from './pages/PaymentCallbackPage';
import Dashboard from './pages/Dashboard';
import SubjectsPage from './pages/SubjectsPage';
import SubjectOverviewPage from './pages/SubjectOverviewPage';
import LecturesPage from './pages/LecturesPage';
import LearningBytesPage from './pages/LearningBytesPage';
import StudySchedulePage from './pages/StudySchedulePage';
import SemesterPlanPage from './pages/SemesterPlanPage';
import ProgressPage from './pages/ProgressPage';
import ProofPage from './pages/ProofPage';
import StudyBuddyPage from './pages/StudyBuddyPage';
import SettingsPage from './pages/SettingsPage';
import AdminPage from './pages/AdminPage';
import RecommendationsPage from './pages/RecommendationsPage';
import StudyGroupsPage from './pages/StudyGroupsPage';
import StudyRoomsPage from './pages/StudyRoomsPage';
import AchievementsPage from './pages/AchievementsPage';
import ReviewPage from './pages/ReviewPage';
import ExamModePage from './pages/ExamModePage';
import ExamAnalyticsPage from './pages/ExamAnalyticsPage';
import CampusPage from './pages/CampusPage';
import ApiDocsPage from './pages/ApiDocsPage';
import StudyPlanPage from './pages/StudyPlanPage';
import ReferralsPage from './pages/ReferralsPage';
import DocumentUploadsPage from './pages/DocumentUploadsPage';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import PricingPage from './pages/PricingPage';
import EducatorsPage from './pages/EducatorsPage';
import ProfilePage from './pages/ProfilePage';
import SupportPage from './pages/SupportPage';
import AddonsPage from './pages/AddonsPage';
import SitemapPage from './pages/SitemapPage';
import ShareViewPage from './pages/ShareViewPage';
import FeedPage from './pages/FeedPage';
import ChallengePage from './pages/ChallengePage';
import LeaderboardsPage from './pages/LeaderboardsPage';
import MarketplacePage from './pages/MarketplacePage';
import TutorsPage from './pages/TutorsPage';
import EducatorDashboardPage from './pages/EducatorDashboardPage';
import MessagesPage from './pages/MessagesPage';
import WalletPage from './pages/WalletPage';
import EducatorAnalyticsPage from './pages/EducatorAnalyticsPage';
import CalendarPage from './pages/CalendarPage';
import ChangelogPage from './pages/ChangelogPage';
import PactsPage from './pages/PactsPage';
import FocusPage from './pages/FocusPage';
import StreakRestorePage from './pages/StreakRestorePage';
import WrappedPage from './pages/WrappedPage';
import FounderPioneerPage from './pages/FounderPioneerPage';
import ClaimPioneerPage from './pages/ClaimPioneerPage';
import Layout from './components/Layout';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  // If user data was passed from AuthCallback, skip auth check
  if (location.state?.user) {
    return children;
  }
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center cyber-bg">
        <div className="text-lg text-white/70">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center cyber-bg">
        <div className="text-lg text-white/70">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  if (!user.is_admin) {
    return <Navigate to="/app" replace />;
  }
  
  return children;
};

// CRITICAL: Check for session_id SYNCHRONOUSLY during render
// useEffect runs AFTER first render - too late for OAuth callback
const AppRouter = () => {
  const location = useLocation();
  const { user, loading } = useAuth();
  
  // Check URL fragment for session_id (OAuth callback)
  // This MUST happen synchronously before other routes process
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center cyber-bg">
        <div className="text-lg text-white/70">Loading...</div>
      </div>
    );
  }
  
  return (
    <>
      <CapacitorDeepLinkHandler />
      <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/developers" element={<DevelopersPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/educators" element={<EducatorsPage />} />
      <Route path="/legal" element={<LegalPage />} />
      <Route path="/login" element={user ? <Navigate to="/app" /> : <LoginPage />} />
      <Route path="/magic-link" element={<MagicLinkPage />} />
      <Route path="/payment/callback" element={<PaymentCallbackPage />} />
      <Route path="/auth/google/callback" element={<GoogleOAuthCallback />} />
      <Route path="/mobile" element={<MobilePage />} />
      {/* Legacy /download still routes to the new Coming-Soon mobile
          page (iter190) — preserves any existing inbound SEO links. */}
      <Route path="/download" element={<Navigate to="/mobile" replace />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/sitemap" element={<SitemapPage />} />
      <Route path="/share/:shareId" element={<ShareViewPage />} />
      <Route path="/challenge/:challengeId" element={<ChallengePage />} />
      <Route path="/home" element={<Navigate to="/" replace />} />
      
      {/* Onboarding - semi-protected (needs user state from callback) */}
      <Route path="/onboarding" element={<OnboardingPage />} />
      
      {/* Protected routes */}
      <Route path="/app" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
      <Route path="/subjects" element={<ProtectedRoute><Layout><SubjectsPage /></Layout></ProtectedRoute>} />
      <Route path="/subjects/:subjectId" element={<ProtectedRoute><Layout><SubjectOverviewPage /></Layout></ProtectedRoute>} />
      <Route path="/lectures" element={<ProtectedRoute><Layout><LecturesPage /></Layout></ProtectedRoute>} />
      <Route path="/learning-bytes" element={<ProtectedRoute><Layout><LearningBytesPage /></Layout></ProtectedRoute>} />
      <Route path="/schedule" element={<ProtectedRoute><Layout><StudySchedulePage /></Layout></ProtectedRoute>} />
      <Route path="/semester-plan" element={<ProtectedRoute><Layout><SemesterPlanPage /></Layout></ProtectedRoute>} />
      <Route path="/progress" element={<ProtectedRoute><Layout><ProgressPage /></Layout></ProtectedRoute>} />
      <Route path="/proof" element={<ProtectedRoute><Layout><ProofPage /></Layout></ProtectedRoute>} />
      <Route path="/study-buddy" element={<ProtectedRoute><Layout><StudyBuddyPage /></Layout></ProtectedRoute>} />
      <Route path="/recommendations" element={<ProtectedRoute><Layout><RecommendationsPage /></Layout></ProtectedRoute>} />
      <Route path="/study-groups" element={<ProtectedRoute><Layout><StudyGroupsPage /></Layout></ProtectedRoute>} />
      <Route path="/study-rooms" element={<ProtectedRoute><Layout><StudyRoomsPage /></Layout></ProtectedRoute>} />
      <Route path="/bookmark-review" element={<ProtectedRoute><Layout><BookmarkReviewPage /></Layout></ProtectedRoute>} />
      <Route path="/achievements" element={<ProtectedRoute><Layout><AchievementsPage /></Layout></ProtectedRoute>} />
      <Route path="/review" element={<ProtectedRoute><Layout><ReviewPage /></Layout></ProtectedRoute>} />
      <Route path="/exam-mode" element={<ProtectedRoute><Layout><ExamModePage /></Layout></ProtectedRoute>} />
      <Route path="/exam-analytics" element={<ProtectedRoute><Layout><ExamAnalyticsPage /></Layout></ProtectedRoute>} />
      <Route path="/campus" element={<ProtectedRoute><Layout><CampusPage /></Layout></ProtectedRoute>} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/changelog" element={<ChangelogPage />} />
      <Route path="/study-plan" element={<ProtectedRoute><Layout><StudyPlanPage /></Layout></ProtectedRoute>} />
      <Route path="/referrals" element={<ProtectedRoute><Layout><ReferralsPage /></Layout></ProtectedRoute>} />
      <Route path="/developer-docs" element={<ProtectedRoute><Layout><ApiDocsPage /></Layout></ProtectedRoute>} />
      <Route path="/document-uploads" element={<ProtectedRoute><Layout><DocumentUploadsPage /></Layout></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><Layout><AnalyticsDashboard /></Layout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Layout><SettingsPage /></Layout></ProtectedRoute>} />
      <Route path="/app/admin" element={<AdminRoute><Layout><AdminPage /></Layout></AdminRoute>} />
      <Route path="/admin" element={<Navigate to="/app/admin" replace />} />
      <Route path="/app/profile" element={<ProtectedRoute><Layout><ProfilePage /></Layout></ProtectedRoute>} />
      <Route path="/profile" element={<Navigate to="/app/profile" replace />} />
      <Route path="/calendar" element={<ProtectedRoute><Layout><CalendarPage /></Layout></ProtectedRoute>}/>
      <Route path="/support" element={<ProtectedRoute><Layout><SupportPage /></Layout></ProtectedRoute>} />
      <Route path="/addons" element={<ProtectedRoute><Layout><AddonsPage /></Layout></ProtectedRoute>} />
      <Route path="/feed" element={<ProtectedRoute><Layout><FeedPage /></Layout></ProtectedRoute>} />
      <Route path="/leaderboards" element={<ProtectedRoute><Layout><LeaderboardsPage /></Layout></ProtectedRoute>} />
      <Route path="/marketplace" element={<ProtectedRoute><Layout><MarketplacePage /></Layout></ProtectedRoute>} />
      <Route path="/tutors" element={<ProtectedRoute><Layout><TutorsPage /></Layout></ProtectedRoute>} />
      <Route path="/educator-dashboard" element={<ProtectedRoute><Layout><EducatorDashboardPage /></Layout></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Layout><MessagesPage /></Layout></ProtectedRoute>} />
      <Route path="/wallet" element={<ProtectedRoute><Layout><WalletPage /></Layout></ProtectedRoute>} />
      <Route path="/educator-analytics" element={<ProtectedRoute><Layout><EducatorAnalyticsPage /></Layout></ProtectedRoute>} />
      <Route path="/pacts" element={<ProtectedRoute><Layout><PactsPage /></Layout></ProtectedRoute>} />
      {/* Distraction-free study mode — intentionally NOT wrapped in <Layout> so
          the sidebar/topnav are hidden, reinforcing the focus reframe. */}
      <Route path="/focus" element={<ProtectedRoute><FocusPage /></ProtectedRoute>} />
      {/* Streak resurrection — email-driven one-click restore. Auth-required
          but intentionally outside Layout so the experience feels like a
          standalone restore moment, not a Dashboard subpage. */}
      <Route path="/streak/restore" element={<ProtectedRoute><StreakRestorePage /></ProtectedRoute>} />
      {/* Wrapped — Spotify-style year-in-review. Full-screen, no Layout
          chrome, so the shareable artifact reads as a standalone moment. */}
      <Route path="/wrapped" element={<ProtectedRoute><WrappedPage /></ProtectedRoute>} />
      <Route path="/wrapped/:year" element={<ProtectedRoute><WrappedPage /></ProtectedRoute>} />
      {/* Founder Pioneer Program — public claim for first 500 pioneers.
          NOT wrapped in ProtectedRoute so unauthenticated visitors can
          see the offer; the claim button itself routes through /login. */}
      <Route path="/founder" element={<FounderPioneerPage />} />
      {/* Pioneer claim landing — magic-link target from invite email (iter191).
          Public so the email link is reachable; the page redirects through
          /login before calling the claim endpoint. */}
      <Route path="/claim-pioneer" element={<ClaimPioneerPage />} />
      
      {/* Catch-all */}
      <Route path="*" element={<Navigate to={user ? '/app' : '/'} replace />} />
    </Routes>
    </>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <TooltipProvider>
      <AuthProvider>
        <PreferencesProvider>
          <NotificationProvider>
            <BrowserRouter>
              <TranscriptionProvider>
                <RecordingProvider>
                  <CelebrationProvider>
                    <AppRouter />
                  </CelebrationProvider>
                </RecordingProvider>
              </TranscriptionProvider>
              <Toaster position="top-right" visibleToasts={3} duration={3000} closeButton gap={8} />
              <OfflineIndicator />
              <AppUpdateBanner />
              <SWUpdatePrompt />
              <PaywallModal />
              <QuickCapture />
              <WhatsNewBanner />
              {/* SupportChatFab is intentionally outside Layout so it
                  appears on PUBLIC pages too (/login, /, /mobile,
                  /pricing). The FAB itself self-hides while the
                  authenticated FocusPage is mounted (z-index respects
                  the focus overlay). */}
              <SupportChatFab />
            </BrowserRouter>
          </NotificationProvider>
        </PreferencesProvider>
      </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
